
import { saveAs } from 'file-saver';
import XLSX from 'xlsx';
import XLSXStyle from 'xlsx-js-style'; // style-preserving SheetJS fork (keeps the template's yellow header)
import PDFDocument from 'pdfkit/js/pdfkit.standalone';
import blobStream from 'blob-stream';

const COLUMN_OPTIONS = [
  { label: 'Identifier Type', value: 'Identifier Type', important: true, optional: false },
  { label: 'Identifier', value: 'Identifier Value', important: true, optional: false },
  { label: 'Item Name', value: 'Item Name', important: true, optional: false },
  { label: 'Brand', value: 'Brand', important: true, optional: false },
  { label: 'Unit Cost', value: 'Unit Cost', important: true, optional: false },
  { label: 'Order Quantity', value: 'Order Quantity', important: true, optional: false },
  { label: 'Invoice Quantity', value: 'Invoice Quantity', important: false, optional: true },
  { label: 'Case Quantity', value: 'Case Quantity', important: false, optional: true },
  { label: 'Notes', value: 'Notes', important: false, optional: true }
];

const EDIT_COLUMN_OPTIONS = [
  { label: 'identifier_type', value: 'identifier_type', important: true, optional: false }, // making identifier type required
  { label: 'identifier_value', value: 'identifier_value', important: true, optional: false },
  { label: 'unit_cost', value: 'unit_cost', important: true, optional: false },
  { label: 'order_quantity', value: 'order_quantity', important: false, optional: true },
  { label: 'invoice_quantity', value: 'invoice_quantity', important: true, optional: false },
  { label: 'case_quantity', value: 'case_quantity', important: false, optional: true },
  { label: 'notes', value: 'notes', important: false, optional: true }
];

const UPLOAD_MAPPING_OPTIONS = [
  { label: 'Identifier', value: 'Identifier Value', important: true, optional: false },
  { label: 'Sku', value: 'Sku', important: true, optional: false }
];

// Single PO file ("Add & Map by Upload File") — MAP ONLY (products are not created). Required:
// Identifier Type, Identifier, Unit Cost, Order Quantity. SKU is optional and sits at the end;
// Ship Qty is optional and only applies when a SKU is present. Item Name/Brand are optional — the
// picking name on the table resolves to the existing vendor item name, else the product name.
const PO_ITEMS_SKU_MAPPING_OPTIONS = [
  { label: 'Identifier Type', value: 'Identifier Type', important: true, optional: false },
  { label: 'Identifier', value: 'Identifier Value', important: true, optional: false },
  { label: 'Item Name', value: 'Item Name', important: false, optional: true },
  { label: 'Brand', value: 'Brand', important: false, optional: true },
  { label: 'Unit Cost', value: 'Unit Cost', important: true, optional: false },
  { label: 'Order Quantity', value: 'Order Quantity', important: true, optional: false },
  { label: 'Case Quantity', value: 'Case Quantity', important: false, optional: true },
  { label: 'Invoice Quantity', value: 'Invoice Quantity', important: false, optional: true },
  { label: 'Notes', value: 'Notes', important: false, optional: true },
  { label: 'SKU', value: 'SKU', important: false, optional: true },
  { label: 'Ship Qty', value: 'Ship Qty', important: false, optional: true }
];

// Combined product-create + sku-mapping upload. Labels match the template headers exactly so that,
// after re-serializing the mapped rows, the backend's camelCase() yields the keys the helpers expect
// (sku, packedQty, identifierValue, identifierType, varietyPack, productName, description, imageUrl,
// brand, vendors). 7 mandatory columns (Sku, Packed Qty, Identifier Value, Identifier Type, Variety
// Pack, Product Name, Vendors); Description / Image URL / Brand are optional. Vendors is comma-separated
// (one vendor item per vendor). Vendor item name is derived from Product Name on the backend.
const PRODUCT_SKU_COLUMN_OPTIONS = [
  { label: 'Sku', value: 'Sku', important: true, optional: false },
  { label: 'Packed Qty', value: 'Packed Qty', important: true, optional: false },
  { label: 'Identifier Value', value: 'Identifier Value', important: true, optional: false },
  { label: 'Identifier Type', value: 'Identifier Type', important: true, optional: false },
  { label: 'Variety Pack', value: 'Variety Pack', important: true, optional: false },
  { label: 'Product Name', value: 'Product Name', important: true, optional: false },
  { label: 'Description', value: 'Description', important: false, optional: true },
  { label: 'Image URL', value: 'Image URL', important: false, optional: true },
  { label: 'Brand', value: 'Brand', important: false, optional: true },
  { label: 'Vendors', value: 'Vendors', important: true, optional: false }
];

const poTemplateDownloadableFile = 'https://opulant-storage.s3.us-east-1.amazonaws.com/po-template-file.xlsx';
const poItemsSkuMappingTemplateDownloadableFile = 'https://opulant-storage.s3.us-east-1.amazonaws.com/po-items-sku-mapping-template-file.xlsx';
const mappingTemplateDownloadableFile = 'https://opulant-storage.s3.us-east-1.amazonaws.com/mapping-template-file.xlsx';
const bulkAddInventorySampleFile = 'https://opulant-storage.s3.us-east-1.amazonaws.com/bulk-add-inventory-file.xlsx';
const initialInventorySampleFile = 'https://opulant-storage.s3.us-east-1.amazonaws.com/initial-inventory-template-file.xlsx';
const initialInventorySellerWarehouseSampleFile = 'https://opulant-storage.s3.us-east-1.amazonaws.com/initial-inventory-template-seller-warehouse.xlsx';
const productSkuTemplateDownloadableFile = 'https://opulant-storage.s3.us-east-1.amazonaws.com/product-sku-template-file.xlsx';

const pickListToAoa = (pickList) => {
  const headers = [['Location', 'Title', 'UPC', 'Brand', 'Quantity', 'Image Url']];
  const data = pickList.map(item => [
    item.location,
    item.item_name,
    item.upc,
    item.brand,
    item.picked_quantity,
    item?.image_url || '',
  ]);
  return [...headers, ...data];
};

// Excel sheet names: 31 chars max, and \ / ? * [ ] are illegal. Falls back to "Shipment N" (n =
// 1-based position among shipment tabs, i.e. excluding the Global View tab) on empty/duplicate names
// so two shipments that only differ in a stripped character don't collide.
const sanitizeSheetName = (name, fallback, usedNames) => {
  const cleaned = String(name || '').replace(/[\\/?*[\]]/g, '').trim().slice(0, 31);
  const base = cleaned || fallback;
  let candidate = base;
  let n = 2;
  while (usedNames.has(candidate)) {
    const suffix = ` (${n})`;
    candidate = `${base.slice(0, 31 - suffix.length)}${suffix}`;
    n += 1;
  }
  usedNames.add(candidate);
  return candidate;
};

// Pack Now, or a Pack Later work order the backend couldn't split (e.g. no shipments resolved
// yet), sends shipments: [] — the workbook then has just the one Global View sheet, identical to
// the file this produced before per-shipment tabs existed.
const generateExcel = ({ pickList, orderName, shipments = [] }) => {
  const workbook = XLSX.utils.book_new();
  const usedNames = new Set();

  const globalSheet = XLSX.utils.aoa_to_sheet(pickListToAoa(pickList));
  XLSX.utils.book_append_sheet(workbook, globalSheet, sanitizeSheetName('Global View', 'Global View', usedNames));

  shipments.forEach((s, index) => {
    const sheet = XLSX.utils.aoa_to_sheet(pickListToAoa(s.pickList || []));
    const sheetName = sanitizeSheetName(s.shipmentName, `Shipment ${index + 1}`, usedNames);
    XLSX.utils.book_append_sheet(workbook, sheet, sheetName);
  });

  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

  saveAs(blob, `${orderName}.xlsx`);
};

const fetchImageAsDataUri = async (url) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error fetching image:', error);
    return null;
  }
};

// helper to check if url is valid and looks like an image
const isValidImageUrl = (url) => {
  try {
    const parsed = new URL(url);
    return (
      ["http:", "https:"].includes(parsed.protocol) &&
      /\.(jpeg|jpg|png|gif|webp|bmp|svg)$/i.test(parsed.pathname)
    );
  } catch {
    return false;
  }
};


const truncateText = (text, maxLength) => {
  return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
};

const generatePDF = async ({ pickList, orderName }) => {
  const doc = new PDFDocument({ size: 'A4', margin: 50 });
  const stream = doc.pipe(blobStream());

  // **Title**
  doc.fontSize(20).text(`Pick List - ${orderName}`, { align: 'center', underline: true }).moveDown(1.5);

  // **Headers**
  const startX = 20;
  const colWidths = [80, 60, 200, 100, 80, 60]; // Column Widths
  const rowHeight = 80; // Height of each row
  let currentY = doc.y;

  doc.fontSize(12).fillColor('black')
    .text('Location', startX, currentY, { width: colWidths[0], bold: true })
    .text('Picture', startX + colWidths[0], currentY, { width: colWidths[1], bold: true })
    .text('Title', startX + colWidths[0] + colWidths[1], currentY, { width: colWidths[2], bold: true })
    .text('UPC', startX + colWidths[0] + colWidths[1] + colWidths[2], currentY, { width: colWidths[3], bold: true })
    .text('Brand', startX + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3], currentY, { width: colWidths[4], bold: true })
    .text('Qty', startX + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3] + colWidths[4], currentY, { width: colWidths[5], bold: true });

  doc.moveTo(startX, doc.y + 15).lineTo(startX + colWidths.reduce((a, b) => a + b, 0) - 30, doc.y + 15).stroke();
  currentY += 39; // Move Y position

  doc.fontSize(12).fillColor('black');

  for (const item of pickList) {
    if (currentY + rowHeight > 750) { // Check if new page is needed
      doc.addPage();
      currentY = 50;
    }

    doc.fontSize(10).text(item.location, startX, currentY, { width: colWidths[0] });

    // **Image**
    if (item.image_url && isValidImageUrl(item.image_url)) {
      try {
        const imageDataUri = await fetchImageAsDataUri(item.image_url);

        if (imageDataUri) {
          doc.image(
            imageDataUri,
            startX + colWidths[0] + 5,
            currentY,
            { width: 30, height: 30 }
          );
        }
      } catch (err) {
        console.warn("Skipping broken image:", item.image_url, err.message);
      }
    } else {
      console.warn("Invalid image URL, skipping:", item.image_url);
    }

    // **Product Details**
    doc.text(item?.item_name, startX + colWidths[0] + colWidths[1], currentY, { width: colWidths[2], continued: true }).text('\n');

    doc.text(item.upc, startX + colWidths[0] + colWidths[1] + colWidths[2] + 10, currentY, { width: colWidths[3] });
    doc.text(item?.brand, startX + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3], currentY, { width: colWidths[4], continued: true }).text('\n');
    doc.text(item.picked_quantity.toString(), startX + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3] + colWidths[4] + 5, currentY, { width: colWidths[5] });

    currentY += rowHeight; // Move down for next row
  }

  // **Finalize PDF**
  doc.end();

  // **Handle PDF Download**
  stream.on('finish', () => {
    const url = stream.toBlobURL('application/pdf');
    const a = document.createElement('a');
    a.href = url;
    a.download = `${orderName}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  });
};



// Export all of the user's SKUs to an .xlsx (mirrors the /inventory/sku page export).
// Pass the rows from GetSKUs({ exportAll: true }).payload.result.
const exportSkusToExcel = (data = [], fileName = 'Skus.xlsx') => {
  const mappedData = (data || []).map((s) => {
    const {
      sku, vendorasin, item_name, status, seller_central, fulfillment_channel,
      amazon_total, amazon_available, warehouse_stock, warehouse_inbound, price,
      total_value, image_url, cogs = '-', adjusted_sales_velocity = '-', is_variety_pack
    } = s || {};
    return {
      'Title': item_name || 'N/A',
      'SKU': sku || 'N/A',
      'ASIN': vendorasin?.asin || 'N/A',
      'Image Url': image_url || 'N/A',
      'Status': status,
      'Listed': seller_central === 1 ? 'True' : 'False',
      'Variety Pack': is_variety_pack ? 'True' : 'False',
      'Cogs': cogs,
      'Adjusted Sales Velocity(90 Days)': adjusted_sales_velocity,
      'Type': fulfillment_channel,
      'Amazon Total': amazon_total || 0,
      'Amazon Available': amazon_available || 0,
      'Warehouse Stock': warehouse_stock || 0,
      'Warehouse Inbound': warehouse_inbound || 0,
      'Selling Price': price != null && !isNaN(price) ? Number(price).toFixed(2) : '0.00',
      'Total Value': total_value != null ? `$${total_value}` : '$0'
    };
  });
  const worksheet = XLSX.utils.json_to_sheet(mappedData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'SKUs');
  XLSX.writeFile(workbook, fileName);
};

// Export all of the user's products to an .xlsx (mirrors the /inventory/products page export).
// Pass the rows from GetProducts({ exportAll: true }).payload.result.
const exportProductsToExcel = (data = [], fileName = 'Products.xlsx') => {
  const mappedData = (data || []).map((product) => {
    const {
      name, brand, identifier_type, identifier_value, warehouse_stock, inbound_stock,
      fba_stock, total_stock, image_url, product_value, cogs = '-', adjusted_sales_velocity = '-'
    } = product || {};
    return {
      'Product Name': name || 'N/A',
      'Image Url': image_url || 'N/A',
      'Brand': brand || 'N/A',
      'Cogs': cogs,
      'Adjusted Sales Velocity(90 Days)': adjusted_sales_velocity,
      'Identifier Type': identifier_type?.toUpperCase() || 'N/A',
      'Identifier Value': identifier_value || 'N/A',
      'Warehouse Stock': warehouse_stock || 0,
      'Inbound Stock': inbound_stock || 0,
      'FBA Stock': fba_stock || 0,
      'Total Stock': total_stock || 0,
      'Inventory Value': product_value != null && !isNaN(product_value)
        ? `$${Number(product_value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : '$0.00'
    };
  });
  const worksheet = XLSX.utils.json_to_sheet(mappedData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Products');
  XLSX.writeFile(workbook, fileName);
};

// Generic: fetch an .xlsx template, find the single column whose header matches `headerMatch`, strip
// the template's dummy sample rows, and fill that column with `values` (everything else left blank).
// Other sheets (e.g. the Instructions sheet) are preserved untouched, and the template's header styling
// (yellow mandatory cells) is kept. Used by Business Onboarding to pre-fill templates instead of the
// heavy full exports.
const fillTemplateColumn = async ({ url, headerMatch, values = [], fileName }) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Template fetch failed (${response.status})`);
  const buffer = await response.arrayBuffer();
  // read + write via xlsx-js-style so the template's header styling (yellow mandatory cells) is kept
  const workbook = XLSXStyle.read(buffer, { type: 'array', cellStyles: true });

  // sheet 1 = data, sheet 2 = instructions (preserved untouched)
  const ws = workbook.Sheets[workbook.SheetNames[0]];
  const range = XLSXStyle.utils.decode_range(ws['!ref']);

  // find the target header column AND fix the header styling: SheetJS reads fills flat on cell.s
  // ({ patternType, fgColor }) but xlsx-js-style WRITES fills from cell.s.fill — re-nest so the
  // template's yellow mandatory headers survive the write.
  let targetCol = -1;
  for (let c = range.s.c; c <= range.e.c; c++) {
    const headerCell = ws[XLSXStyle.utils.encode_cell({ r: range.s.r, c })];
    if (!headerCell) continue;
    if (headerMatch(`${headerCell.v}`.trim())) targetCol = c;
    const s = headerCell.s;
    if (s && !s.fill && (s.patternType || s.fgColor || s.bgColor)) {
      headerCell.s = {
        ...s,
        fill: {
          patternType: s.patternType || 'solid',
          ...(s.fgColor ? { fgColor: s.fgColor } : {}),
          ...(s.bgColor ? { bgColor: s.bgColor } : {})
        }
      };
    }
  }
  if (targetCol === -1) targetCol = range.s.c; // fallback: first column

  const startRow = range.s.r + 1;
  const origEndRow = range.e.r;

  // the template ships with dummy sample rows — clear every data cell below the header so only the
  // target column we fill below has data
  for (let r = startRow; r <= origEndRow; r++) {
    for (let c = range.s.c; c <= range.e.c; c++) {
      delete ws[XLSXStyle.utils.encode_cell({ r, c })];
    }
  }

  // write each value into the target column, starting on the row after the header
  (values || []).forEach((val, i) => {
    ws[XLSXStyle.utils.encode_cell({ r: startRow + i, c: targetCol })] = { t: 's', v: `${val}` };
  });

  // range = header row + the filled rows (drops the cleared dummy rows)
  range.e.r = Math.max(range.s.r, startRow + Math.max(0, (values?.length || 0) - 1));
  ws['!ref'] = XLSXStyle.utils.encode_range(range);

  XLSXStyle.writeFile(workbook, fileName);
};

// Pre-fill the product-sku upload template's data sheet with the user's SKU codes (Sku column only).
// Used by Business Onboarding step 1 instead of the heavy full SKU export.
const fillProductSkuTemplate = (skus = [], fileName = 'product-sku-template.xlsx') =>
  fillTemplateColumn({
    url: productSkuTemplateDownloadableFile,
    headerMatch: (header) => header.toLowerCase() === 'sku',
    values: skus,
    fileName
  });

// Pre-fill the initial-inventory upload template's data sheet with the user's product identifier values
// (Product Identifier column only). The seller+warehouse template has no User Email column, so it uses a
// different file. Used by Business Onboarding step 2 instead of the heavy full products export.
const fillInventoryTemplate = (
  identifiers = [],
  { sellerWarehouse = false, fileName = 'initial-inventory-template.xlsx' } = {}
) =>
  fillTemplateColumn({
    url: sellerWarehouse ? initialInventorySellerWarehouseSampleFile : initialInventorySampleFile,
    headerMatch: (header) => header.toLowerCase() === 'product identifier',
    values: identifiers,
    fileName
  });

export {
  COLUMN_OPTIONS,
  EDIT_COLUMN_OPTIONS,
  UPLOAD_MAPPING_OPTIONS,
  PO_ITEMS_SKU_MAPPING_OPTIONS,
  PRODUCT_SKU_COLUMN_OPTIONS,
  poTemplateDownloadableFile,
  mappingTemplateDownloadableFile,
  poItemsSkuMappingTemplateDownloadableFile,
  productSkuTemplateDownloadableFile,
  generateExcel,
  generatePDF,
  exportSkusToExcel,
  exportProductsToExcel,
  fillProductSkuTemplate,
  fillInventoryTemplate,
  bulkAddInventorySampleFile,
  initialInventorySampleFile,
  initialInventorySellerWarehouseSampleFile
};
